import React, { useEffect, type JSX } from 'react';
import { Paper } from '@mantine/core';
import WelcomeWalkthroughStyled from './WelcomeWalkthroughStyled';

interface WelcomeWalkthroughStepProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Wraps the WelcomeWalkthroughStyled component for the walkthrough flow
 * Uses a fixed position overlay to ensure consistency with the ConsentWalkthroughStep
 */
export function WelcomeWalkthroughStep({ opened, onClose }: WelcomeWalkthroughStepProps): JSX.Element | null {
  // Add a warning when users try to navigate away or refresh
  useEffect(() => {
    if (opened) {
      // Add event handler for beforeunload
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        // This message may not be displayed in some browsers that
        // use their own generic message for security reasons
        const message = "You haven't completed the welcome walkthrough. Are you sure you want to leave?";
        e.preventDefault();
        e.returnValue = message;
        return message;
      };
      
      // Add event handler
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      // Clean up
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [opened]);

  // If not opened, don't render anything
  if (!opened) {
    return null;
  }

  // Use a fixed position overlay with max z-index to prevent any interaction with background
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999, // Higher than any other z-index in the app
        overflow: 'auto'
      }}
    >
      <div className="max-w-full w-full md:w-auto px-4 py-8">
        <Paper p={0} radius="lg" shadow="xl">
          <WelcomeWalkthroughStyled onComplete={onClose} />
        </Paper>
      </div>
    </div>
  );
}